import { useOutletContext } from "react-router-dom";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";

import PageAbstract from "components/PageAbstract";
import socialAccounts from "socialAccounts";
import _request from "actions/_request";
import i18n from "i18";
import enterPrice from "../../images/enterPack.svg";
import businessPrice from "../../images/businessPack.svg";
import professionalPrice from "../../images/proffPack.svg";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import _helper from "actions/_helper";
import AppStoreBillingNotice from "components/AppStoreBillingNotice";
import { isAppleBilled } from "utils/billing";

import "./style.css";

const socialAccountsArr = Object.keys(socialAccounts);

class PricingPage extends PageAbstract {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      resp: null,
      items: [],
      pageLoading: false,
      period: "monthly",
      openDialog: false,
    };
  }

  refresh(cb) {
    cb();
  }

  // A plan billed by the App Store is changed there; the server refuses Stripe
  // checkout for it (OP#2998).
  isAppleBilled() {
    return isAppleBilled(this.props.metaData);
  }

  tr(key) {
    return i18n.t("pricingPage." + key);
  }

  isMonthly() {
    return this.state.period === "monthly";
  }

  TrialPeriodInfo = ({ isActive, userPackage }) => {
    const isFreeVersion = userPackage.id === 1;

    return (
      <Box>
        <h3>{this.tr("freeVersionDetails")}</h3>
        {isFreeVersion && isActive ? (
          <>
            <p>{this.tr("freeVersionActive")}</p>
            <p>{this.tr("freeVersionAccess")}</p>
            <ul>
              <li>{this.tr("oneAccount")}</li>
              <li>{this.tr("fourPostsMonth")}</li>
            </ul>
            <p>{this.tr("freeVersionPackage")}</p>
            <ul>
              <li>{this.tr("postsTabAccessible")}</li>
              <li>{this.tr("addingAccountsRestricted")}</li>
            </ul>
          </>
        ) : (
          <p>{this.tr("noFreeVersionAccess")}</p>
        )}
      </Box>
    );
  };

  handleTrialClick() {
    this.setState({ openDialog: true });
  }

  handleConfirm = async () => {
    this.setState({ openDialog: false });
    await _request.users.loading(async () => {
      await _request.stripe.freeVersion();
    });
  };

  handleCancel = () => {
    this.setState({ openDialog: false });
  };

  handleClick = async (id, isMonthly) => {
    if (this.isAppleBilled()) return;
    try {
      const appOrigin = window.location.origin;
      const successUrl = `${appOrigin}/app/stripe/success`;
      const failedUrl = `${appOrigin}/app/stripe/failed?error=true`;

      const response = await _request.stripe.post("payment/stripe", {
        duration: isMonthly ? "monthly" : "yearly",
        package: id,
        successUrl,
        failedUrl,
        cancelUrl: failedUrl,
        success_url: successUrl,
        failed_url: failedUrl,
        cancel_url: failedUrl,
      });

      if (response && response.data) {
        this.setState({
          resp: response,
          items: response.data.items || [],
          pageLoading: false,
        });
        const redirectUrl = response.data;

        if (typeof redirectUrl === "string" && redirectUrl.startsWith("http")) {
          window.location.href = redirectUrl;
        } else {
          console.error("Invalid redirect URL:", redirectUrl);
        }
      } else {
        console.error("Response data is missing or invalid.");
      }
    } catch (error) {
      console.error("Error in handleClick:", error);
    }
  };

  handleChange = (event) => {
    this.setState({ period: event.target.checked ? "yearly" : "monthly" });
  };

  dialogModal() {
    return (
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "var(--background-menu)",
            borderRadius: "16px",
            padding: "8px",
          },
        }}
        open={this.state.openDialog}
        onClose={this.handleCancel}>
        <DialogTitle
          sx={{
            color: "var(--text-color)",
            fontWeight: "bold",
            fontSize: "20px",
          }}>
          {this.tr("confirmFreeVersion")}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ color: "var(--text-color)" }}
            variant="body1"
            paragraph>
            {this.tr("switchToFreeConfirm")}
          </Typography>
          <Typography
            sx={{ color: "var(--text-color)" }}
            variant="body1"
            paragraph>
            {this.tr("losePaidPackage")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "red" }}
            paragraph>
            {this.tr("paidFeaturesWarning")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "8px 16px 16px" }}>
          <Box onClick={this.handleCancel} className="pricing-btn-cancel">
            {this.tr("cancel")}
          </Box>
          <Box onClick={this.handleConfirm} className="pricing-btn-confirm">
            {this.tr("confirm")}
          </Box>
        </DialogActions>
      </Dialog>
    );
  }

  renderBillingToggle() {
    const { period } = this.state;
    const isYearly = period === "yearly";

    return (
      <div className="pricing-billing-toggle">
        <button
          className={`pricing-period-btn ${!isYearly ? "pricing-period-btn--active" : ""}`}
          onClick={() => this.setState({ period: "monthly" })}>
          {this.tr("monthly")}
        </button>
        <button
          className={`pricing-period-btn ${isYearly ? "pricing-period-btn--active" : ""}`}
          onClick={() => this.setState({ period: "yearly" })}>
          {this.tr("yearly")}
          <span className="pricing-save-badge">-20%</span>
        </button>
      </div>
    );
  }

  renderFreeVersionBanner(userPackage) {
    const isActive = userPackage.id === 1;
    const buttonText = isActive ? this.tr("freeVersion") : this.tr("try");

    return (
      <div className="pricing-banner pricing-banner--free">
        <div className="pricing-banner__content">
          <div className="pricing-banner__info">
            <div className="pricing-banner__title-row">
              <span className="pricing-banner__title">
                {this.tr("freeVersion")}
              </span>
              <Tooltip
                title={
                  <this.TrialPeriodInfo
                    isActive={isActive}
                    userPackage={userPackage}
                  />
                }
                placement="right">
                <InfoIcon
                  sx={{
                    fontSize: 18,
                    color: "var(--text-color)",
                    opacity: 0.7,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            </div>
            <span className="pricing-banner__subtitle">
              {this.tr("oneAccount")} &bull; {this.tr("fourPostsMonth")}
            </span>
          </div>
          <button
            className={`pricing-action-btn ${isActive ? "pricing-action-btn--disabled" : ""}`}
            disabled={isActive}
            onClick={() => !isActive && this.handleTrialClick()}>
            {buttonText}
          </button>
        </div>
      </div>
    );
  }

  renderCustomBanner() {
    return (
      <div className="pricing-banner pricing-banner--custom">
        <div className="pricing-banner__content">
          <div className="pricing-banner__info">
            <span className="pricing-banner__title">{this.tr("custom")}</span>
            <span className="pricing-banner__subtitle">
              {this.tr("customPrice")}
            </span>
          </div>
          <button className="pricing-action-btn pricing-action-btn--outline">
            {this.tr("selectPlan")}
          </button>
        </div>
      </div>
    );
  }

  renderFeatureRow(text, key) {
    return (
      <li key={key} className="pricing-feature-row">
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, color: "#42c0c7", flexShrink: 0 }}
        />
        <span>{text}</span>
      </li>
    );
  }

  renderCardFeatures(perms, limitsRaw) {
    const { watermark, video, photo, storage } = perms;

    let limits = limitsRaw;
    if (typeof limits === "string") {
      try {
        limits = JSON.parse(limits);
      } catch {
        limits = {};
      }
    }
    if (!limits || typeof limits !== "object") limits = {};

    // Plan limits live in `limits`, not in `perms` (which only holds platform booleans).
    // -1 means unlimited — render as ∞, matching the storage convention below.
    const fmtLimit = (v) => (v === -1 ? "∞" : v ?? 0);
    const accs = fmtLimit(limits.accounts?.total);
    const grs = fmtLimit(limits.account_groups);
    const posts = fmtLimit(limits.scheduled_posts);
    const aiCreditsMonthly = limits.ai_credits_monthly ?? null;
    const soc = socialAccountsArr.filter((key) => perms[key]).length;
    const storageMb = limits.storage_mb ?? storage ?? 0;
    const isUnlimited = storageMb === -1;
    const size = isUnlimited ? "∞" : _helper.formatBytes((storageMb || 0) * 1048576);
    const progressPercentage = isUnlimited ? 100 : (storageMb ? Math.min(storageMb / 100, 100) : 0);

    const accText = this.tr("optionAccounts").replace("$1", accs);
    const socialText = this.tr("optionSocial").replace("$1", soc);
    const grText = this.tr("optionGroups").replace("$1", grs);
    const postsText = this.tr("optionPosts").replace("$1", posts);

    const fileTypes = [];
    if (photo) fileTypes.push(i18n.t("fileType.image"));
    if (video) fileTypes.push(i18n.t("fileType.video"));

    const features = [
      accText,
      socialText,
      grText,
      postsText,
      ...(aiCreditsMonthly !== null
        ? [
            aiCreditsMonthly === 0
              ? this.tr("optionAiCreditsNone")
              : this.tr("optionAiCredits").replace(
                  "$1",
                  aiCreditsMonthly === -1 ? "∞" : aiCreditsMonthly
                ),
          ]
        : []),
      this.tr("premiumSupport"),
      ...(watermark ? [this.tr("watermark")] : []),
      ...(perms["image-editor"] ? [this.tr("imageEditor")] : []),
      ...(fileTypes.length > 0
        ? [`${this.tr("fileTypes")}: ${fileTypes.join(", ")}`]
        : []),
    ];

    return (
      <div className="pricing-card__features">
        <ul className="pricing-feature-list">
          {features.map((text, i) => this.renderFeatureRow(text, i))}
        </ul>
        <div className="pricing-storage">
          <div className="pricing-storage__header">
            <span>{this.tr("storage")}</span>
            <span className="pricing-storage__value">{size}</span>
          </div>
          <div className="pricing-storage__bar">
            <div
              className="pricing-storage__fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  getImageById(title) {
    switch (title) {
      case "Professional":
        return enterPrice;
      case "Business":
        return businessPrice;
      case "Enterprise":
        return professionalPrice;
      default:
        return null;
    }
  }

  renderCard(currId, key, info) {
    const { id, title, price_annually_amount, price_monthly_amount, permissions, highlight } = info;
    const isMonthly = this.isMonthly();
    const isCurr = currId === id;
    const isPopular = !!highlight;

    let perms = permissions;
    if (typeof perms === "string") {
      try {
        perms = JSON.parse(perms);
      } catch {
        perms = {};
      }
    }
    if (!perms || typeof perms !== "object") perms = {};

    const price = parseFloat(isMonthly ? price_monthly_amount : price_annually_amount) || 0;
    const priceLabel = isMonthly ? this.tr("monthly") : this.tr("yearly");
    const packageImage = this.getImageById(title);

    return (
      <div
        key={key}
        className={`pricing-card ${isPopular ? "pricing-card--popular" : ""} ${isCurr ? "pricing-card--active" : ""}`}>
        {isPopular && (
          <div className="pricing-card__badge">{this.tr("popular")}</div>
        )}
        {packageImage && (
          <div className="pricing-card__image-wrap">
            <img
              src={packageImage}
              alt={title}
              className="pricing-card__image"
            />
          </div>
        )}
        <div className="pricing-card__body">
          <div className="pricing-card__header">
            <h3 className="pricing-card__title">{title}</h3>
            <div className="pricing-card__price-row">
              <span className="pricing-card__price">${price}</span>
              <span className="pricing-card__price-period">
                &nbsp;/ {priceLabel}
              </span>
            </div>
          </div>
          <button
            className={`pricing-card__cta ${isCurr ? "pricing-card__cta--active" : ""}`}
            disabled={isCurr || this.isAppleBilled()}
            onClick={() => !isCurr && this.handleClick(id, isMonthly)}>
            {isCurr
              ? this.tr("active")
              : this.isAppleBilled()
                ? i18n.t("billing.appStoreShort")
                : this.tr("select")}
          </button>
          {this.renderCardFeatures(perms, info.limits)}
        </div>
      </div>
    );
  }

  renderCards(currId, packages) {
    const safePackages = Array.isArray(packages) ? packages : [];
    const filtered = safePackages
      .filter(
        (it) =>
          it.is_purchasable &&
          it.is_public &&
          parseFloat(it.price_monthly_amount) !== 99999,
      )
      .sort((a, b) => a.sort - b.sort);

    return filtered.map((it, i) => this.renderCard(currId, i, it));
  }

  renderPage() {
    const metaData = this.props.metaData || {};
    const packages =
      (Array.isArray(metaData.packages) && metaData.packages) ||
      (Array.isArray(metaData.pricing?.packages) &&
        metaData.pricing.packages) ||
      [];
    const userPackage =
      metaData.userPackage ||
      metaData.subscription?.package ||
      metaData.user ||
      {};
    const { id } = userPackage;

    return (
      <div className="pricing-page">
        <div className="pricing-page__header">
          <h1 className="pricing-page__title">{this.tr("mainTitle")}</h1>
          {this.renderBillingToggle()}
        </div>

        {this.isAppleBilled() ? (
          <AppStoreBillingNotice />
        ) : (
          this.renderFreeVersionBanner(userPackage)
        )}

        <div className="pricing-cards-grid">
          {this.renderCards(id, packages)}
        </div>

        {this.renderCustomBanner()}
        {this.dialogModal()}
      </div>
    );
  }
}

export default function Pricing() {
  return <PricingPage {...useOutletContext()} />;
}
